import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, toastApiError } from "../lib/api";
import { fmtTL } from "../lib/format";
import { CalendarDays, Calculator, AlertTriangle, ListChecks } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import SavedPlans from "./SavedPlans";
import ActualBudget from "./ActualBudget";
import PlanEntryPanel from "./budget/PlanEntryPanel";
import { DayBody } from "./budget/DayRows";
import { AYLAR } from "./budget/dailyBudgetCalc";
import { useDailyBudgetModel } from "./budget/useDailyBudgetModel";
import { readJson, writeJson } from "../lib/storage";

const readSaved = () => readJson("dailyBudget", {});
const currentMonth = () => { const now = new Date(); return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`; };
const LEFT_HEADERS = ["GÜN", "ANA BÜTÇE KALEMİ", "ALT BÜTÇE KALEMİ"];
const NUM_HEADERS = ["PLANLANAN GELİR", "GERÇEKLEŞEN GELİR", "GELİR BÜTÇE FARKI", "PLANLANAN GİDER", "GERÇEKLEŞEN GİDER", "GİDER BÜTÇE FARKI"];

function OvershootBanner({ m, y, totalPlan, exp }) {
  const pct = ((exp / totalPlan - 1) * 100).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
  return (
    <div className="px-5 py-2.5 bg-red-50 border-b border-red-200 flex items-center gap-2 text-xs text-red-800" data-testid="plan-overshoot-banner">
      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
      <span><b>{AYLAR[m - 1]} {y}</b> ayında planlı kalemlerin planı aşıldı — Plan: <b>{fmtTL(totalPlan)}</b>, Gerçekleşen (planlı kalemler): <b>{fmtTL(exp)}</b> (<b>%{pct}</b> aşım)</span>
    </div>
  );
}

export default function DailyBudget() {
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [saved] = useState(readSaved);
  const [month, setMonth] = useState(saved.month || currentMonth());
  const [items, setItems] = useState([]);
  const [daily, setDaily] = useState({ days: [], plans: {} });
  const [openDays, setOpenDays] = useState(saved.openDays || {});
  const [openLeaves, setOpenLeaves] = useState(saved.openLeaves || {});
  const [planOpen, setPlanOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const toggleLeaf = (k) => setOpenLeaves((o) => ({ ...o, [k]: !o[k] }));

  // ESC ile geri dönüldüğünde ay, açık gün ve açık alt kalem korunur
  useEffect(() => { writeJson("dailyBudget", { month, openDays, openLeaves }); }, [month, openDays, openLeaves]);
  useEffect(() => { http.get("/budget-items").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const loadDaily = useCallback(() => http.get(`/reports/daily-budget?month=${month}`).then((r) => setDaily(r.data)).catch((e) => toastApiError(e)), [month]);
  useEffect(() => { loadDaily(); }, [loadDaily]);

  // kayıttan ESC ile dönüş (?odak=<gün>-<kalem>): gün + alt kalemi aç, satıra kaydır ve yanıp söndür
  const odak = params.get("odak");
  useEffect(() => {
    if (!odak || odak.startsWith("actual-")) return;
    const [day, ...rest] = odak.split("-");
    const leafId = rest.join("-");
    if (!day || !leafId) return;
    setOpenDays((o) => ({ ...o, [day]: true }));
    setOpenLeaves((o) => ({ ...o, [`${day}-${leafId}`]: true }));
    const t = setTimeout(() => {
      const row = document.querySelector(`[data-testid="day-${Number(day)}-leaf-${leafId}"]`);
      if (row) { row.scrollIntoView({ behavior: "smooth", block: "center" }); row.classList.add("flash-row"); setTimeout(() => row.classList.remove("flash-row"), 4500); }
      setParams((p) => { const n = new URLSearchParams(p); n.delete("odak"); return n; }, { replace: true });
    }, 600);
    return () => clearTimeout(t);
  }, [odak, daily, setParams]);

  const { byId, parents, childrenOf, leafs, dayMap, y, m, dayCount, dayList, totalPlan, cumByDay, todayDay, calc, lastExp } = useDailyBudgetModel(items, daily, month);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="daily-budget">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <CalendarDays className="w-4 h-4 text-slate-600" />
        <span className="text-sm font-semibold">Günlük Bütçe Takibi</span>
        <span className="text-xs text-slate-500">Gün satırına tıklayın — ana kalemler koyu, alt kalemler normal görünür</span>
        <div className="ml-auto flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="daily-month-picker"
            className="px-2.5 py-1.5 text-xs border border-slate-300 rounded-md bg-white outline-none focus:border-amber-500" />
          <button onClick={() => setSavedOpen(!savedOpen)} data-testid="saved-plans-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${savedOpen ? "bg-sky-700 text-white border-sky-700" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
            <ListChecks className="w-3.5 h-3.5" /> Kayıtlı Planlar
          </button>
          {isAdmin && (
            <button onClick={() => setPlanOpen(!planOpen)} data-testid="plan-entry-toggle"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${planOpen ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
              <Calculator className="w-3.5 h-3.5" /> Plan Girişi
            </button>
          )}
        </div>
      </div>

      {totalPlan > 0 && lastExp > totalPlan && <OvershootBanner m={m} y={y} totalPlan={totalPlan} exp={lastExp} />}

      {planOpen && isAdmin && <PlanEntryPanel month={month} y={y} m={m} dayCount={dayCount} dayList={dayList} leafs={leafs} byId={byId} onClose={() => setPlanOpen(false)} onSaved={loadDaily} />}

      {savedOpen && <SavedPlans year={y} month={m} items={items} byId={byId} onClose={() => setSavedOpen(false)} />}
      <ActualBudget month={month} />

      <div className="overflow-x-auto">
        <table className="w-full tbl">
          <thead className="bg-slate-50">
            <tr>
              {LEFT_HEADERS.map((h) => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{h}</th>)}
              {NUM_HEADERS.map((h) => (
                <th key={h} className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{h}{h.startsWith("PLANLANAN") && <span className="block text-[9px] font-normal normal-case text-slate-400">Planlanan günde: toplam plan, gerçekleşmeler, kalan</span>}</th>
              ))}
            </tr>
          </thead>
          {dayList.map((d) => (
            <DayBody key={d} d={d} dayItems={dayMap[d]} open={!!openDays[d]} onToggle={() => setOpenDays((o) => ({ ...o, [d]: !o[d] }))} parents={parents} childrenOf={childrenOf} leafs={leafs}
              calc={calc} totalPlan={totalPlan} cumDay={cumByDay[d]} todayDay={todayDay} openLeaves={openLeaves} toggleLeaf={toggleLeaf} />
          ))}
        </table>
      </div>
    </div>
  );
}
